import type { HighscoreEntry, ThreatLevelId } from "../core/MetaState.ts";
import { DEFAULT_THREAT_LEVEL, THREAT_LEVEL_IDS } from "../core/MetaState.ts";
import type { RankedEntry } from "../persistence/LeaderboardClient.ts";
import type { EventBus } from "../core/EventBus.ts";
import { getBootStyles } from "./BootSequenceStyles.ts";
import { createMascot } from "./OperatorMascot.ts";
import {
  formatScoreDate,
  renderLocalScoreRows,
} from "./BootSequenceTemplate.ts";
import {
  getBootHTML,
  runPostPhase,
  startLiveAnimations,
  type BootSequenceActions,
} from "./BootSequenceDOM.ts";
import { GearMenu } from "./GearMenu.ts";
import { t } from "../utils/i18n.ts";

/**
 * Escape a score-row name for the innerHTML seam. LOCAL/D1 initials are pinned
 * to INITIALS_CHARSET by the backend validator (no HTML-significant chars), but
 * since 2026-08-18 GLOBAL rows can carry raw Game Center aliases — free-form
 * text Apple validates, not us — so the board escapes what it interpolates.
 * Same minimal idiom as TradingCard's local escapeHtml.
 */
function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** BL-130 — the boot-screen gear's settings hooks, threaded from main.ts.
 *  S146/BL-142: the text-size (S/M/L) control was retired — the app is L-only,
 *  so the boot gear carries Language → Audio → Legal. */
export interface BootSettingsBridge {
  language: "en" | "de";
  bus?: EventBus;
  onSetLanguage?: (lang: "en" | "de") => void;
}

/**
 * BL-143 (S147) — the boot ambience hooks, threaded from main.ts and backed
 * by AudioEngine. The boot screen owns the audio while the music director is
 * silent (BACK fades the theme out; the theme itself now starts at the
 * title reveal via the SEPARATE `onTitleReveal` hook below, not through this
 * bridge), so these two cues play the loading terminal only. Absent → the
 * boot runs silent (graceful; the UI never depends on audio).
 *
 * Task-001 (2026-audio-polish, AC-2) — shrunk from three cues to two: the
 * title floppy loop (`titleLoopIn`/`titleLoopOut`) is retired end-to-end —
 * the theme now owns the whole title→menu→deploy arc instead (see
 * `onTitleReveal`).
 */
export interface BootAudioBridge {
  /** Loading-grit bed fades in as the boot/POST terminal starts. */
  loadingAmbienceIn(): void;
  /** Loading-grit bed fades out at the boot→title hand-off (no title overlap). */
  loadingAmbienceOut(): void;
  /** One-shot alarm sting on the first red threat line (may bleed into title). */
  threatSting(): void;
}

const IGNORED_KEYS = new Set([
  "Meta",
  "Control",
  "Shift",
  "Alt",
  "CapsLock",
  "Tab",
  "Fn",
]);

interface BootSequenceShowOptions {
  localHighscores?: HighscoreEntry[];
  enabled?: boolean;
  /**
   * POPULATION SWITCHING (owner spec 2026-08-17). Supply these three together
   * and the board grows a `‹ THREAT LEVEL ›` row: local, global and the native
   * Game Center board then all answer for the SAME visible population, and the
   * player can page through all five. Omit them and the board behaves exactly
   * as before (one population, no switcher) — which is what the web build and
   * the existing tests do.
   *
   * The switcher is VIEW-ONLY on purpose: paging the board must never change
   * which level the next run is played at. That belongs to the deploy selector,
   * and silently rewriting it from a highscore browse would be a trap.
   */
  initialBoardLevel?: ThreatLevelId;
  /** Local rows for one population (already sorted, best first). */
  localHighscoresFor?: (level: ThreatLevelId) => HighscoreEntry[];
  /** Global rows for one population. MUST throw when the source is
   *  unreachable — an empty array means "this board has no entries yet", and
   *  the two states read differently to the player. */
  fetchGlobalFor?: (level: ThreatLevelId) => Promise<RankedEntry[]>;
  // BL-95: the "this row is you" highlight rides the per-row `self` flag the
  // server sets (fetchGlobal is fetched with includeSelf). No stable playerId
  // is threaded through the UI any more.
  fetchGlobal?: () => Promise<RankedEntry[]>;
  /** WOW Pass 2 (item 1) — the iOS Game Center board is enabled but the player
   *  is NOT signed in: the GLOBAL pane shows a "SIGN IN TO COMPETE" CTA + the
   *  local best instead of fetching (the compete front door, graceful degrade). */
  globalSignedOut?: boolean | undefined;
  /** WOW Pass 2 (item 1) — optional "VIEW GLOBAL RANKS" action (pops the native
   *  Game Center board). Omitted → no button. */
  onViewGlobalRanks?: ((level: ThreatLevelId) => void) | undefined;
  /** BL-130 — the settings bridge for the boot-screen ⚙ gear (language ·
   *  text-size · audio · legal). Omitted → the gear shows Legal only. */
  settings?: BootSettingsBridge;
  /** BL-143 — the boot ambience hooks (grit + sting only). Omitted → the
   *  boot runs silent. */
  audio?: BootAudioBridge;
  /** Task-001 (2026-audio-polish, AC-3) — fired once per title reveal
   *  (`startPhase3()`, incl. every re-entrant call via `skipToIdle()`/a
   *  language re-render — the callee is expected to be idempotent, exactly
   *  like `MusicDirector.enterTitle()`). Omitted → no title-reveal signal
   *  (the boot still runs; just no theme start hook). */
  onTitleReveal?: () => void;
  /**
   * DEVICE ROUND 2026-08-18 (owner): open directly on the settled TITLE
   * screen instead of replaying the POST terminal. Used by PreRun's BACK —
   * "zurück" must land on the start screen, not on a boot animation the
   * player already sat through this launch.
   */
  startAtTitle?: boolean;
}

/**
 * Boot Sequence — Retro arcade mainframe terminal with tower defense animation.
 * Phases: POST → Panel Slam → Idle (press any key).
 */
export class BootSequence {
  private overlay: HTMLDivElement;
  private onComplete: (() => void) | null = null;
  private phase = 0;
  private skipReady = false;
  private timers: number[] = [];
  private destroyed = false;
  private intervals: number[] = [];
  private localHighscores: HighscoreEntry[] | undefined;
  private leaderboardEnabled = false;
  private fetchGlobal: (() => Promise<RankedEntry[]>) | null = null;
  private globalFetchToken = 0;
  /** WOW Pass 2 (item 1) — GC enabled but signed-out: show the compete CTA +
   *  local best instead of fetching; optional native-board action. */
  private globalSignedOut = false;
  private onViewGlobalRanks: ((level: ThreatLevelId) => void) | null = null;
  /** The population the board is CURRENTLY showing (view-only, see options). */
  private boardLevel: ThreatLevelId = DEFAULT_THREAT_LEVEL;
  private localHighscoresFor:
    | ((level: ThreatLevelId) => HighscoreEntry[])
    | null = null;
  private fetchGlobalFor:
    | ((level: ThreatLevelId) => Promise<RankedEntry[]>)
    | null = null;
  /** BL-130 — the shared top-right ⚙ gear + its settings bridge + the lazily
   *  built legal surface it opens. */
  private gear: GearMenu | null = null;
  private settings: BootSettingsBridge | null = null;
  /** B31 (2026-08-15): der Typ kommt per `import type`, die Klasse erst beim
   *  Klick — sonst haengt der komplette Rechtstext-Korpus im Boot-Chunk. */
  private legalOverlay: import("./LegalOverlay.ts").LegalOverlay | null = null;
  private gearUnsubs: Array<() => void> = [];
  /** BL-143 — the boot ambience bridge (grit + sting; null → silent, graceful). */
  private audio: BootAudioBridge | null = null;
  /** Task-001 (2026-audio-polish, AC-3) — the title-reveal hook (null → no-op). */
  private onTitleReveal: (() => void) | null = null;
  /** P1-3 — idle throttle: after IDLE_MS with no input on the title screen, the
   *  live metric/glitch loops are cleared and CSS animations paused (battery).
   *  Any tap/key/move resumes. Kept OUT of `this.timers` so cleanup() (which
   *  enterIdle itself calls) doesn't fight it. */
  private idleTimer: number | null = null;
  private isIdle = false;
  private static readonly IDLE_MS = 45000;

  constructor(parent: HTMLElement) {
    this.overlay = document.createElement("div");
    this.overlay.id = "boot-sequence";
    this.injectStyles();
    parent.appendChild(this.overlay);
    this.overlay.style.display = "none";
  }

  show(onComplete: () => void, options: BootSequenceShowOptions = {}): void {
    this.onComplete = onComplete;
    this.localHighscores = options.localHighscores;
    this.leaderboardEnabled = options.enabled ?? false;
    this.fetchGlobal = options.fetchGlobal ?? null;
    this.globalSignedOut = options.globalSignedOut ?? false;
    this.onViewGlobalRanks = options.onViewGlobalRanks ?? null;
    this.localHighscoresFor = options.localHighscoresFor ?? null;
    this.fetchGlobalFor = options.fetchGlobalFor ?? null;
    this.boardLevel = options.initialBoardLevel ?? DEFAULT_THREAT_LEVEL;
    if (this.localHighscoresFor) {
      this.localHighscores = this.localHighscoresFor(this.boardLevel);
    }
    this.settings = options.settings ?? null;
    this.audio = options.audio ?? null;
    this.onTitleReveal = options.onTitleReveal ?? null;
    this.globalFetchToken++;
    this.overlay.classList.remove("boot-flash");
    this.overlay.style.display = "flex";
    this.overlay.innerHTML = "";
    this.phase = 0;
    this.skipReady = false;
    this.destroyed = false;
    this.buildInterface();
    this.buildGear();
    if (
      this.leaderboardEnabled &&
      (this.fetchGlobalFor || this.fetchGlobal) &&
      !this.globalSignedOut
    ) {
      void this.loadGlobalHighscores(this.globalFetchToken);
    }
    if (!options.startAtTitle) {
      // BL-143 — the loading grit bed fades in as the boot terminal starts.
      this.audio?.loadingAmbienceIn();
      this.startPhase1();
    }
    this.delay(1000, () => {
      this.skipReady = true;
    });

    const handler = (e: Event): void => {
      if (e instanceof KeyboardEvent && IGNORED_KEYS.has(e.key)) return;
      // BL-130 — while the ⚙ gear is open it owns the keyboard (Escape closes
      // it); never let a keypress there skip/transition the boot.
      if (this.gear?.isOpen()) return;
      if (!this.skipReady) return;
      if (this.phase < 3) {
        e.preventDefault();
        this.skipToIdle();
      } else if (e instanceof KeyboardEvent) {
        e.preventDefault();
        this.transition();
      }
    };
    this.overlay.addEventListener("keydown", handler);
    this.overlay.tabIndex = 0;
    this.overlay.focus();

    // P1-3 — any input resets the idle throttle (and resumes if idle).
    const activity = (): void => this.onUserActivity();
    this.overlay.addEventListener("pointerdown", activity, { passive: true });
    this.overlay.addEventListener("pointermove", activity, { passive: true });
    this.overlay.addEventListener("keydown", activity);

    this.overlay.addEventListener("click", (e: Event) => {
      if (!this.skipReady) return;
      if (this.phase < 3) {
        e.preventDefault();
        this.skipToIdle();
        return;
      }
      const target = e.target as HTMLElement;
      const press = this.overlay.querySelector("#boot-press");
      if (press?.contains(target)) {
        e.preventDefault();
        this.transition();
      }
    });

    if (options.startAtTitle) {
      // DEVICE ROUND 2026-08-18: BACK from PreRun lands on the settled TITLE
      // screen — no POST replay. Runs AFTER every handler above is bound so
      // the title stays fully interactive; skipToIdle() fires onTitleReveal
      // idempotently (the caller's theme hook) and cleanup() inside it kills
      // the skipReady delay timer, so the flag is set directly.
      this.skipToIdle();
      this.skipReady = true;
    }
  }

  hide(): void {
    this.overlay.style.display = "none";
    // BL-143 — defensive: never leave the boot grit LOOP running past the
    // screen (idempotent — the normal phase-3 hand-off already faded it out).
    // The one-shot threat sting is left to finish on its own. Task-001
    // (2026-audio-polish, AC-3) — the theme (raised at the title reveal) must
    // SURVIVE this hand-off (it owns the whole title→menu→deploy arc), so
    // this deliberately does NOT touch it.
    this.audio?.loadingAmbienceOut();
    this.cleanup();
    this.teardownGear();
  }

  destroy(): void {
    this.destroyed = true;
    this.cleanup();
    this.teardownGear();
    this.overlay.remove();
  }

  private cleanup(): void {
    // Intro timers/intervals only — NOT the gear (skipToIdle() calls cleanup but
    // the idle screen keeps its gear). The gear is torn down in hide()/destroy().
    for (const t of this.timers) clearTimeout(t);
    this.timers = [];
    for (const i of this.intervals) clearInterval(i);
    this.intervals = [];
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  // ── P1-3 — idle throttle (title screen) ─────────────────────────────────────

  /** (Re)arm the idle countdown — only meaningful on the live title screen. */
  private armIdleWatch(): void {
    if (this.idleTimer !== null) clearTimeout(this.idleTimer);
    this.idleTimer = window.setTimeout(
      () => this.enterIdle(),
      BootSequence.IDLE_MS
    );
  }

  /** Pause the live loops + CSS animation after the idle timeout. */
  private enterIdle(): void {
    if (this.destroyed || this.phase < 3 || this.isIdle) return;
    this.isIdle = true;
    // Stop the metric/clock/glitch loops (clears intervals + their reschedule
    // timers); the gear stays (it lives outside these timers).
    this.cleanup();
    this.overlay.classList.add("boot-idle");
  }

  /** Resume the live loops + CSS animation on the next input. */
  private exitIdle(): void {
    if (!this.isIdle) return;
    this.isIdle = false;
    this.overlay.classList.remove("boot-idle");
    if (this.phase === 3 && !this.destroyed) {
      startLiveAnimations(this.overlay, this.getActions());
    }
  }

  /** Any user input on the title screen: resume if idle, then re-arm. */
  private onUserActivity(): void {
    if (this.phase < 3 || this.destroyed) return;
    if (this.isIdle) this.exitIdle();
    this.armIdleWatch();
  }

  /** BL-130 — drop the gear (its open click-away/Escape listeners) + the
   *  master-mute subscription + the legal surface. */
  private teardownGear(): void {
    for (const u of this.gearUnsubs) u();
    this.gearUnsubs = [];
    this.gear?.destroy();
    this.gear = null;
    this.legalOverlay?.destroy();
    this.legalOverlay = null;
  }

  private delay(ms: number, fn: () => void): void {
    this.timers.push(window.setTimeout(fn, ms));
  }

  private interval(ms: number, fn: () => void): void {
    this.intervals.push(window.setInterval(fn, ms));
  }

  private getActions(): BootSequenceActions {
    return {
      delay: (ms, fn) => this.delay(ms, fn),
      interval: (ms, fn) => this.interval(ms, fn),
      isDestroyed: () => this.destroyed,
      getPhase: () => this.phase,
      // BL-143 — the threat sting, fired once with the first red threat line.
      onFirstThreat: () => this.audio?.threatSting(),
    };
  }

  private buildInterface(): void {
    const initialGlobalMarkup = this.globalSignedOut
      ? this.signedOutMarkup()
      : this.leaderboardEnabled
      ? `<div class="boot-score-offline">GLOBAL - LOADING...</div>`
      : `<div class="boot-score-offline">GLOBAL - UNAVAILABLE (LOCAL BUILD)</div>`;
    this.overlay.innerHTML = getBootHTML(
      this.localHighscores,
      initialGlobalMarkup,
      t(`threatLevel.${this.boardLevel}`)
    );
    this.bindLeaderboardToggle();
    this.bindLevelSwitcher();
    this.bindViewGlobalRanks();
    this.buildMascotDuo();
  }

  /**
   * WOW Pass 2 (item 1) — the GLOBAL-pane graceful degrade when Game Center is
   * present but the player isn't signed in: a "SIGN IN TO COMPETE" CTA + the
   * local best, so the compete front door reads even signed-out (never blocks).
   * Static markup (reduced-motion-safe); the optional VIEW GLOBAL RANKS button
   * pops the native board (wired in bindViewGlobalRanks).
   */
  private signedOutMarkup(): string {
    const best = this.localHighscores?.[0]?.score ?? 0;
    const ranksBtn = this.onViewGlobalRanks
      ? `<button id="boot-view-ranks" class="boot-score-ranks-btn" type="button">${t(
          "boot.viewGlobalRanks"
        )}</button>`
      : "";
    return `<div class="boot-score-signin">${t(
      "boot.signInToCompete"
    )}</div><div class="boot-score-localbest">${t("boot.localBest", {
      value: best.toLocaleString(),
    })}</div>${ranksBtn}`;
  }

  /** WOW Pass 2 (item 1) — wire the optional VIEW GLOBAL RANKS button (native
   *  Game Center board). Re-bound on each (re)render (e.g. a language flip).
   *  Opens the board of the population the player is LOOKING AT, so Apple's own
   *  screen can never contradict ours. */
  private bindViewGlobalRanks(): void {
    if (!this.onViewGlobalRanks) return;
    const btn = this.overlay.querySelector("#boot-view-ranks");
    btn?.addEventListener("click", () =>
      this.onViewGlobalRanks?.(this.boardLevel)
    );
  }

  /**
   * The `‹ THREAT LEVEL ›` population switcher. Only live when the caller
   * supplied level-keyed sources; otherwise the row is removed entirely rather
   * than shown dead — a control that does nothing is worse than no control.
   */
  private bindLevelSwitcher(): void {
    const row = this.overlay.querySelector(".boot-score-level");
    if (!row) return;
    if (!this.localHighscoresFor && !this.fetchGlobalFor) {
      row.remove();
      return;
    }
    const step = (delta: number): void => {
      const n = THREAT_LEVEL_IDS.length;
      const i = THREAT_LEVEL_IDS.indexOf(this.boardLevel);
      // Wrap, so five taps in one direction return you where you started.
      const next = THREAT_LEVEL_IDS[(i + delta + n) % n];
      if (!next) return;
      this.setBoardLevel(next);
    };
    this.overlay
      .querySelector("#boot-level-prev")
      ?.addEventListener("click", () => step(-1));
    this.overlay
      .querySelector("#boot-level-next")
      ?.addEventListener("click", () => step(+1));
  }

  /** Switch the visible population: label, local rows and global rows together
   *  — they must never disagree about which board is on screen. */
  private setBoardLevel(level: ThreatLevelId): void {
    this.boardLevel = level;
    const label = this.overlay.querySelector("#boot-level-label");
    if (label) label.textContent = t(`threatLevel.${level}`);
    this.renderLocalPane();
    if (this.globalSignedOut) {
      // Still signed out — the CTA's "local best" must follow the population.
      this.updateGlobalPane(this.signedOutMarkup());
      this.bindViewGlobalRanks();
      return;
    }
    if (this.leaderboardEnabled && (this.fetchGlobalFor || this.fetchGlobal)) {
      this.updateGlobalPane(
        `<div class="boot-score-offline">GLOBAL - LOADING...</div>`
      );
      this.globalFetchToken++;
      void this.loadGlobalHighscores(this.globalFetchToken);
    }
  }

  /** Repaint the LOCAL pane for the currently shown population. */
  private renderLocalPane(): void {
    if (this.localHighscoresFor) {
      this.localHighscores = this.localHighscoresFor(this.boardLevel);
    }
    const pane = this.overlay.querySelector("#boot-scores-local");
    if (pane) pane.innerHTML = renderLocalScoreRows(this.localHighscores);
  }

  /**
   * WOW Pass 2 (item 1) — Game Center can authenticate AFTER the board first
   * painted (its first-launch sheet/handshake outlives boot, or the player signs
   * in later via the native board). When it does, drop the "SIGN IN TO COMPETE"
   * CTA and pull the GLOBAL rows live — no relaunch. No-op if the board isn't
   * GC-enabled, has no fetcher, or is already showing GLOBAL.
   */
  notifyGlobalAuthenticated(): void {
    if (
      !this.leaderboardEnabled ||
      !(this.fetchGlobalFor || this.fetchGlobal) ||
      !this.globalSignedOut
    ) {
      return;
    }
    this.globalSignedOut = false;
    this.updateGlobalPane(
      `<div class="boot-score-offline">GLOBAL - LOADING...</div>`
    );
    this.globalFetchToken++;
    void this.loadGlobalHighscores(this.globalFetchToken);
  }

  // ── BL-130 — the shared top-right ⚙ gear (one gear on Boot/PreRun/HUD) ──────

  /** Build the boot-screen gear in the canonical order: Language → Text size →
   *  Audio → Legal. Content varies (Boot omits the in-run controls + Reset); the
   *  order never does. With no settings bridge the gear carries Legal only. */
  private buildGear(): void {
    this.gear?.destroy();
    for (const u of this.gearUnsubs) u();
    this.gearUnsubs = [];

    const host = document.createElement("div");
    host.className = "boot-gear-host";
    this.gear = new GearMenu({ ns: "boot" });
    const g = this.gear;
    const s = this.settings;

    if (s && s.onSetLanguage) {
      g.section(t("setting.section"));
      g.appendContent(this.gearLine(this.buildLanguageButton(s)));
    }

    if (s?.bus) {
      g.divider();
      g.section(t("hud.audio.section"));
      g.appendContent(this.gearLine(this.buildMuteButton(s.bus)));
    }

    g.divider();
    g.appendContent(this.gearLine(this.buildLegalButton()));

    g.attach(host);
    this.overlay.appendChild(host);
  }

  /** Wrap a folded control as a full-width gear-dropdown line. */
  private gearLine(control: HTMLElement): HTMLElement {
    const line = document.createElement("div");
    line.className = "boot-gear-line";
    line.appendChild(control);
    return line;
  }

  /** EN/DE toggle — flips + persists the locale (host hook), then re-renders the
   *  boot screen in the new language (jumping straight to the ready state). */
  private buildLanguageButton(s: BootSettingsBridge): HTMLElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "boot-gear-btn-pill";
    btn.textContent = `🌐 ${s.language.toUpperCase()}`;
    btn.title = t("setting.language");
    btn.setAttribute("aria-label", `${t("setting.language")}: ${s.language}`);
    btn.addEventListener("click", () => {
      const next = s.language === "de" ? "en" : "de";
      s.language = next;
      s.onSetLanguage?.(next);
      this.rebuildForLanguage();
    });
    return btn;
  }

  /** Master-mute toggle — emits on the bus and reflects the echoed state. */
  private buildMuteButton(bus: EventBus): HTMLElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "boot-gear-btn-pill";
    let muted = false;
    const render = (): void => {
      btn.textContent = `♪ ${t("audio.audio")}`;
      btn.classList.toggle("muted", muted);
      btn.setAttribute("aria-pressed", String(muted));
      btn.setAttribute("aria-label", t(muted ? "audio.unmute" : "audio.mute"));
    };
    render();
    btn.addEventListener("click", () => bus.emit("audio.toggleMaster"));
    this.gearUnsubs.push(
      bus.on("audio.masterMuteChanged", (m: unknown) => {
        muted = m as boolean;
        render();
      })
    );
    return btn;
  }

  /** LEGAL — opens the in-app legal/privacy surface (lazily built, reused). */
  private buildLegalButton(): HTMLElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "boot-gear-btn-pill";
    btn.textContent = `⚖ ${t("legal.open")}`;
    btn.title = t("legal.title");
    btn.setAttribute("aria-label", t("legal.title"));
    btn.addEventListener("click", () => {
      this.gear?.close();
      // Der Overlay-Code wird erst beim ersten Oeffnen geholt (B31). Der
      // Handler bleibt synchron; das Laden laeuft daneben.
      void (async () => {
        if (!this.legalOverlay) {
          const { LegalOverlay } = await import("./LegalOverlay.ts");
          this.legalOverlay = new LegalOverlay();
        }
        this.legalOverlay.show("privacy");
      })();
    });
    return btn;
  }

  /** Re-render the boot screen in the active locale, then settle to the ready
   *  state (a language flip means the player is past the intro animation). */
  private rebuildForLanguage(): void {
    this.buildInterface();
    // skipToIdle() runs cleanup() (which would tear down a gear) — so (re)build
    // the gear AFTER settling to the ready state.
    this.skipToIdle();
    this.buildGear();
    if (
      this.leaderboardEnabled &&
      (this.fetchGlobalFor || this.fetchGlobal) &&
      !this.globalSignedOut
    ) {
      this.globalFetchToken++;
      void this.loadGlobalHighscores(this.globalFetchToken);
    }
  }

  private bindLeaderboardToggle(): void {
    const localBtn = this.overlay.querySelector(
      "#boot-scores-local-btn"
    ) as HTMLButtonElement | null;
    const globalBtn = this.overlay.querySelector(
      "#boot-scores-global-btn"
    ) as HTMLButtonElement | null;
    const localPane = this.overlay.querySelector("#boot-scores-local");
    const globalPane = this.overlay.querySelector("#boot-scores-global");
    if (!localBtn || !globalBtn || !localPane || !globalPane) return;

    const setMode = (mode: "local" | "global"): void => {
      const localActive = mode === "local";
      localBtn.classList.toggle("boot-score-tab-active", localActive);
      globalBtn.classList.toggle("boot-score-tab-active", !localActive);
      localPane.classList.toggle("boot-score-panel-active", localActive);
      globalPane.classList.toggle("boot-score-panel-active", !localActive);
    };

    localBtn.addEventListener("click", () => setMode("local"));
    globalBtn.addEventListener("click", () => setMode("global"));
    setMode(this.leaderboardEnabled ? "global" : "local");
  }

  /**
   * Fill the GLOBAL pane, distinguishing THREE outcomes that used to collapse
   * into one misleading line.
   *
   * Until 2026-08-18 an empty result and a failed fetch both rendered
   * "GLOBAL - OFFLINE". On the device gate that produced a real false alarm:
   * Game Center answered correctly with zero rows (the board genuinely had no
   * scores) and the screen claimed the player was offline. The fetcher contract
   * is now explicit — **throwing means unreachable, `[]` means empty** — and
   * the two are shown differently.
   */
  private async loadGlobalHighscores(fetchToken: number): Promise<void> {
    const fetcher = this.fetchGlobalFor
      ? () => this.fetchGlobalFor!(this.boardLevel)
      : this.fetchGlobal;
    if (!fetcher) return;

    try {
      const entries = await fetcher();
      if (this.destroyed || fetchToken !== this.globalFetchToken) return;

      if (entries.length > 0) {
        this.updateGlobalPane(this.renderGlobalRows(entries));
        return;
      }

      // Reached the board, and it is simply empty — a brand-new population has
      // no entries until somebody plays it. Saying "offline" here would be a
      // false statement about the network.
      this.updateGlobalPane(
        `<div class="boot-score-offline">GLOBAL - NO ENTRIES YET</div>`
      );
    } catch {
      if (this.destroyed || fetchToken !== this.globalFetchToken) return;
      this.updateGlobalPane(
        `<div class="boot-score-offline">GLOBAL - OFFLINE</div>`
      );
    }
  }

  private updateGlobalPane(markup: string): void {
    const pane = this.overlay.querySelector(
      "#boot-scores-global"
    ) as HTMLElement | null;
    if (!pane) return;
    pane.innerHTML = markup;
  }

  /**
   * All fetched rows, in rank order — the COUNT policy lives at the fetch site
   * (main.ts asks for 10; owner decision 2026-08-18: top-10 + own rank), the
   * pane itself is a fixed-height scroll viewport (styles) so a longer list can
   * never push the frozen boot layout. A discontinuity in the rank sequence
   * (top-10 … then the player's own row at rank 102342) renders as a `⋯` separator
   * row — data-driven, so an adjacent own rank (#11) needs no special case.
   * The own row keeps `boot-score-row-self`, which the styles pin to the
   * bottom of the scroll viewport (sticky) so the player's rank is visible
   * without scrolling.
   */
  private renderGlobalRows(entries: RankedEntry[]): string {
    let prevRank: number | null = null;
    return entries
      .map((entry) => {
        const gap =
          prevRank !== null && entry.rank > prevRank + 1
            ? `<div class="boot-score-gap" aria-hidden="true">&ctdot;</div>`
            : "";
        prevRank = entry.rank;
        const selfClass = entry.self ? " boot-score-row-self" : "";
        // Game Center rows carry NO date (createdAt "") — omit the cell
        // instead of rendering a dead "--/--/--" column; the styles give the
        // freed width to the name (GC aliases run far longer than the three
        // D1 initials, and the own-rank cell must fit a six-digit rank).
        const date = entry.createdAt
          ? `<span class="boot-score-date">${formatScoreDate(
              entry.createdAt
            )}</span>`
          : "";
        return `${gap}<div class="boot-score-row${selfClass}"><span class="boot-score-rank">#${
          entry.rank
        }</span><span class="boot-score-name">${escapeHtml(
          entry.initials
        )}</span>${date}<span class="boot-score-pts">${entry.score.toLocaleString()}</span></div>`;
      })
      .join("\n            ");
  }

  /**
   * VERA + FENRIR standing inside the AI-CORE hero area, flanking the hex core.
   * VERA = left (.boot-aifig.p), FENRIR = right (.boot-aifig.o).
   */
  private buildMascotDuo(): void {
    const vera = this.overlay.querySelector("#boot-fig-vera") as HTMLElement;
    const fenrir = this.overlay.querySelector(
      "#boot-fig-fenrir"
    ) as HTMLElement;
    if (vera) {
      vera.appendChild(
        createMascot({
          character: "vera",
          heroArt: "start", // S144/BL-135: full-head start-screen hero
          size: 150,
          frame: false,
          className: "boot-mascot boot-mascot-vera",
        })
      );
    }
    if (fenrir) {
      fenrir.appendChild(
        createMascot({
          character: "fenrir",
          heroArt: "start", // S144/BL-135: full-head start-screen hero
          size: 130,
          frame: false,
          className: "boot-mascot boot-mascot-fenrir",
        })
      );
    }
  }

  private startPhase1(): void {
    this.phase = 1;
    runPostPhase(this.overlay, this.getActions(), () => this.startPhase2());
  }

  private startPhase2(): void {
    this.phase = 2;
    const postEl = this.overlay.querySelector("#boot-post") as HTMLElement;
    if (postEl) postEl.classList.add("post-fadeout");
    const grid = this.overlay.querySelector(".boot-grid") as HTMLElement;
    if (grid) grid.style.opacity = "1";

    const panels =
      this.overlay.querySelectorAll<HTMLElement>('[data-phase="2"]');
    let i = 0;
    const slam = (): void => {
      if (this.destroyed || i >= panels.length) {
        this.delay(200, () => this.startPhase3());
        return;
      }
      panels[i]!.classList.add("panel-in");
      i++;
      this.delay(80 + Math.random() * 40, slam);
    };
    this.delay(400, slam);
  }

  private startPhase3(): void {
    this.phase = 3;
    // BL-143 — the boot→title hand-off: grit out (so it never overlaps the
    // title). Task-001 (2026-audio-polish, AC-3) — the retired title floppy
    // loop is replaced by the title-reveal hook, which raises the THEME
    // instead (MusicDirector.enterTitle(), idempotent). Reached on the
    // normal slam→idle path AND via skipToIdle() / a language re-render;
    // both calls stay idempotent (the callees own that guard).
    this.audio?.loadingAmbienceOut();
    this.onTitleReveal?.();
    const ctaBlock = this.overlay.querySelector(
      ".boot-cta-block"
    ) as HTMLElement;
    if (ctaBlock)
      ctaBlock.parentElement?.classList.add("boot-cta-area", "panel-in");
    const footer = this.overlay.querySelector(".boot-footer") as HTMLElement;
    if (footer) footer.classList.add("panel-in");
    startLiveAnimations(this.overlay, this.getActions());
    // P1-3 — begin watching for idle now the live title screen is up.
    this.armIdleWatch();
  }

  private skipToIdle(): void {
    this.cleanup();
    const postEl = this.overlay.querySelector("#boot-post") as HTMLElement;
    if (postEl) postEl.style.display = "none";
    const grid = this.overlay.querySelector(".boot-grid") as HTMLElement;
    if (grid) grid.style.opacity = "1";
    this.overlay
      .querySelectorAll<HTMLElement>("[data-phase]")
      .forEach((el) => el.classList.add("panel-in"));
    this.startPhase3();
  }

  private transition(): void {
    // PRESS SPACE → hand off to the menu (onComplete→showMainMenu). Task-001
    // (2026-audio-polish, AC-3) — the theme (already running since the title
    // reveal) must survive this hand-off nonstop, so unlike the retired
    // title-floppy path this deliberately does NOT fade anything out here;
    // `hide()` (called right after) is likewise theme-silent now.
    this.overlay.classList.add("boot-flash");
    this.delay(200, () => {
      this.hide();
      this.onComplete?.();
    });
  }

  private injectStyles(): void {
    if (document.getElementById("boot-seq-styles")) return;
    const style = document.createElement("style");
    style.id = "boot-seq-styles";
    style.textContent = getBootStyles();
    document.head.appendChild(style);
  }
}
